import { useMemo, useState } from 'react'
import { FontAwesomeSvgIcon } from 'react-fontawesome-svg-icon'

import { faGaugeMin } from '@fortawesome/pro-light-svg-icons'
import { Bar, ComposedChart, Legend, ResponsiveContainer, Scatter, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts'
import { ContentType } from 'recharts/types/component/DefaultLegendContent'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'

import { Text } from '@infura/packages.ui/Text'

import { InfuraUsage } from 'components/billing/usage/usage-types'
import {
  BAR_HEIGHT,
  THRESHOLD_NAMES,
  ThresholdName,
  ThresholdShape,
  backgroundColor,
  thresholdColor
} from 'components/dashboard/analytics-overview/DailyCreditUsageChart/DailyCreditUsageChart'
import { TooltipContent } from 'components/dashboard/analytics-overview/DailyCreditUsageChart/TooltipContent'

import { getFormattedUsage } from 'lib/usage/getTotalUsage'

type DailyCreditUsageChartProps = {
  limit: number
}

const chartColorsWithBackground = ['#d4d4d4'] as Array<string>

const renderLegend: ContentType = () => (
  <div className='text-text-default flex max-w-[1100px] px-4 py-2 text-center' data-testid='daily-credit-usage-no-data'>
    No data available yet. Send requests to see your daily request usage for today.
  </div>
)

export const DailyCreditUsageEmpty = ({ limit }: DailyCreditUsageChartProps) => {
  const [focusSeries, setFocusSeries] = useState<string | null>(null)

  const NO_DATA = {
    groupedItems: [],
    labels: [],
    timeseries: [
      {
        date: 'dummy',
        eightyFivePercent: limit * 0.85,
        ninetyPercent: limit * 0.9,
        seventyFivePercent: limit * 0.75
      }
    ]
  } as InfuraUsage.TimeseriesData

  const CustomTooltip = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (
          active &&
          payload?.length &&
          (NO_DATA.labels.find(({ text }) => text === focusSeries) ||
            THRESHOLD_NAMES.includes(focusSeries as ThresholdName))
        ) {
          return <TooltipContent focusSeries={focusSeries} payload={payload} />
        }
        return null
      },
    [focusSeries]
  )

  return (
    <div className='flex h-full flex-col justify-end py-6'>
      <div>
        <div className='flex justify-between'>
          <div data-testid='daily-credit-usage-value'>
            <Text as='span' className='text-2xl font-bold'>
              0
            </Text>
            <Text alternative={true} as='span'>
              {' '}
              of {getFormattedUsage(limit)} used
            </Text>
          </div>
          <div className='text-text-alternative flex items-center gap-1'>
            <FontAwesomeSvgIcon icon={faGaugeMin} size='sm' />
            <div data-testid='daily-credit-usage-available'>{getFormattedUsage(limit)} available</div>
          </div>
        </div>
        <div className='xs:h-[250px] desktop:h-[190px] wide:h-[140px] xxl:h-[100px] h-[380px] sm:h-[200px] md:h-[230px]'>
          <ResponsiveContainer>
            <ComposedChart data={NO_DATA.timeseries} layout='vertical'>
              <YAxis dataKey='date' hide type='category' />
              <XAxis axisLine={false} domain={[0, limit]} hide type='number' />
              <Tooltip
                content={<CustomTooltip />}
                cursor={false}
                isAnimationActive={false}
                position={{ y: focusSeries && THRESHOLD_NAMES.includes(focusSeries as ThresholdName) ? 0 : -150 }}
              />
              <Legend align='left' content={renderLegend} verticalAlign='bottom' />
              {/* add a background item to the payload so on background hover the tooltip is not visible */}
              {[
                {
                  dataKey: '',
                  text: 'background',
                  tooltipText: 0
                },
                ...NO_DATA.labels
              ].map((label, index) => {
                if (!NO_DATA.groupedItems.includes(label.text)) {
                  return (
                    <Bar
                      barSize={BAR_HEIGHT}
                      dataKey={label.text}
                      fill={chartColorsWithBackground[index] as string}
                      key={label.text}
                      stackId='a'
                      {...(index === 0 && { background: { fill: backgroundColor, radius: 2 } })}
                    />
                  )
                }
              })}
              <Scatter
                dataKey='seventyFivePercent'
                fill={thresholdColor}
                onMouseEnter={() => {
                  setFocusSeries('seventyFivePercent')
                }}
                onMouseLeave={() => {
                  setFocusSeries(null)
                }}
                shape={<ThresholdShape />}
              />
              <Scatter
                dataKey='eightyFivePercent'
                fill={thresholdColor}
                onMouseEnter={() => {
                  setFocusSeries('eightyFivePercent')
                }}
                onMouseLeave={() => {
                  setFocusSeries(null)
                }}
                shape={<ThresholdShape />}
              />
              <Scatter
                dataKey='ninetyPercent'
                fill={thresholdColor}
                onMouseEnter={() => {
                  setFocusSeries('ninetyPercent')
                }}
                onMouseLeave={() => {
                  setFocusSeries(null)
                }}
                shape={<ThresholdShape />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
